/* ══════════════════════════════════════════════════════════════════════
   10_BRAND SHEET  —  brand × pack × state pivot

     row A:  |        |       | State      | Values   |
     row B:  |        |       | Bihar      | Bihar    | Chhattisgarh | … |
     row C:  | IN SUB-BRAND | IN PACK SIZE | Sum of WD% | Sum of VPO | … |
     data :  | CAMPA LEMON  | 200.00 ML    | 10.50%     | 1          | … |

   Each state occupies a PAIR of columns (WD%, VPO). The state row repeats
   the name across both, so a forward fill is harmless either way.

   WD% arrives as a percentage ("10.50%" or 0.105); VPO is a plain number.
   Both are normalised here: wd is a fraction (0.105), vpo a float.
   ══════════════════════════════════════════════════════════════════════ */

export const BRAND_SHEET = /10[_\s-]*brand|brand.*10|sub[_\s-]*brand/i;

const txt = (v) => (v === null || v === undefined ? "" : String(v).trim());

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/,/g, "").replace(/%/g, "").replace(/\s/g, ""));
  return isNaN(n) ? null : n;
};

/** "10.50%" -> 0.105 ; 0.105 -> 0.105 ; 10.5 -> 0.105 */
const pct = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v > 1.5 ? v / 100 : v;
  const s = String(v).trim();
  const n = num(s);
  if (n === null) return null;
  return s.includes("%") || n > 1.5 ? n / 100 : n;
};

const METRIC_RE = /sum\s*of\s*(wd\s*%?|vpo)|^(wd\s*%?|vpo)$/i;
const isWD = (s) => /wd/i.test(s);

function forwardFill(row, width, from = 0) {
  const out = new Array(width).fill("");
  let last = "";
  for (let i = from; i < width; i++) {
    const v = txt(row?.[i]);
    if (v) last = v;
    out[i] = last;
  }
  return out;
}

/** Short display label: "CAMPA LEMON" + "200.00 ML" -> "Campa Lemon 200" */
export function shortLabel(brand, pack) {
  const ml = String(pack || "").match(/([\d.]+)\s*ml/i);
  const size = ml ? String(Math.round(Number(ml[1]))) : "";
  const b = String(brand || "")
    .replace(/\s*sugar\s*$/i, "")
    .replace(/thums\s*up\s*xforce/i, "TUX")
    .replace(/rim\s*-?\s*zim/i, "Rim-Zim")
    .trim();
  const title = b.length <= 4 ? b
    : b.split(/\s+/).map(w => (w.length <= 3 && w === w.toUpperCase() ? w
        : w[0].toUpperCase() + w.slice(1).toLowerCase())).join(" ");
  return size ? `${title} ${size}` : title;
}

export function parseBrandSheet(raw, sheetName = "") {
  const fail = (why) => ({ __fail: why, sheetName });
  if (!raw?.length) return fail("sheet is empty");

  const width = Math.max(...raw.map(r => (r || []).length), 0);
  if (width < 4) return fail(`only ${width} columns`);

  // ── Metric row: most "Sum of WD% / Sum of VPO" tokens ──
  let metricIdx = -1, best = 0;
  for (let i = 0; i < Math.min(20, raw.length); i++) {
    const hits = (raw[i] || []).filter(c => METRIC_RE.test(txt(c))).length;
    if (hits > best) { best = hits; metricIdx = i; }
  }
  if (metricIdx < 1 || best < 2) return fail(`no "Sum of WD% / Sum of VPO" row found (best had ${best})`);

  // ── Label columns: brand then pack, to the left of the first metric ──
  let firstMetricCol = width;
  for (let i = 0; i < width; i++) {
    if (METRIC_RE.test(txt(raw[metricIdx]?.[i]))) { firstMetricCol = i; break; }
  }
  if (firstMetricCol < 1) return fail("metric columns start at column 0 — no label column");
  const brandCol = 0;
  const packCol = firstMetricCol >= 2 ? firstMetricCol - 1 : -1;

  // ── State row: nearest non-empty row above, within the metric span ──
  let stateIdx = -1;
  for (let i = metricIdx - 1; i >= 0 && i >= metricIdx - 3; i--) {
    const filled = (raw[i] || []).filter((c, ci) => ci >= firstMetricCol && txt(c)).length;
    if (filled) { stateIdx = i; break; }
  }
  if (stateIdx < 0) return fail("no state header row above the metric row");
  const states = forwardFill(raw[stateIdx], width, firstMetricCol);

  // ── Column plan: one {wd, vpo} pair per state ──
  const stateCols = {};
  const stateOrder = [];
  for (let i = firstMetricCol; i < width; i++) {
    const st = states[i];
    const m = txt(raw[metricIdx]?.[i]);
    if (!st || !m || !METRIC_RE.test(m)) continue;
    if (/^(state|values|grand\s*total|total)$/i.test(st)) continue;
    if (!stateCols[st]) { stateCols[st] = {}; stateOrder.push(st); }
    if (isWD(m)) { if (stateCols[st].wd === undefined) stateCols[st].wd = i; }
    else if (stateCols[st].vpo === undefined) stateCols[st].vpo = i;
  }
  if (!stateOrder.length) return fail("no state columns resolved");

  // ── Data rows ──
  const rows = [];
  for (let i = metricIdx + 1; i < raw.length; i++) {
    const r = raw[i];
    if (!r) continue;
    const brand = txt(r[brandCol]);
    if (!brand) continue;
    if (/^grand\s*total$/i.test(brand)) continue;

    const pack = packCol >= 0 ? txt(r[packCol]) : "";
    const values = {};
    let any = false;
    stateOrder.forEach(st => {
      const c = stateCols[st];
      const wd = c.wd !== undefined ? pct(r[c.wd]) : null;
      const vpo = c.vpo !== undefined ? num(r[c.vpo]) : null;
      if ((wd != null && wd !== 0) || (vpo != null && vpo !== 0)) any = true;
      values[st] = { wd, vpo };
    });
    if (!any) continue;

    rows.push({
      brand, pack,
      key: `${brand}|${pack}`,
      label: shortLabel(brand, pack),
      values,
    });
  }
  if (!rows.length) return fail("header parsed but no data rows had values");

  // Rank series by how widely they appear, so the default pick is meaningful
  const reach = (r) => stateOrder.reduce((s, st) => s + ((r.values[st]?.wd || 0) > 0 ? 1 : 0), 0);
  const series = rows.map(r => ({
    key: r.key, label: r.label, brand: r.brand, pack: r.pack, reach: reach(r),
  })).sort((a, b) => b.reach - a.reach);

  return {
    states: stateOrder,
    rows,
    series,
    byKey: Object.fromEntries(rows.map(r => [r.key, r])),
    layout: { metricIdx, stateIdx, brandCol, packCol, firstMetricCol, width },
  };
}

export function findBrandSheet(workbook, XLSX) {
  const read = (n) => XLSX.utils.sheet_to_json(workbook.Sheets[n],
    { header: 1, raw: true, defval: null });
  const debug = [];

  const attempt = (name) => {
    let parsed;
    try { parsed = parseBrandSheet(read(name), name); }
    catch (e) { debug.push({ sheet: name, why: `threw: ${e.message}` }); return null; }
    if (parsed?.__fail) { debug.push({ sheet: name, why: parsed.__fail }); return null; }
    return { ...parsed, sheetName: name };
  };

  const named = workbook.SheetNames.filter(n => BRAND_SHEET.test(n));
  for (const name of named) { const hit = attempt(name); if (hit) return { brand: hit, debug }; }
  for (const name of workbook.SheetNames) {
    if (named.includes(name)) continue;
    const hit = attempt(name); if (hit) return { brand: hit, debug };
  }
  return { brand: null, debug };
}

/* ── Defaults matching the source deck ── */
export const DEFAULT_BRAND_STATES = [
  "Telangana", "Uttar Pradesh", "West Bengal", "Maharashtra",
  "Orissa", "Gujarat", "Delhi", "Karnataka",
];

/* Brand+pack series the deck plots, in its order. Matched loosely so a
   different pack size in the file still resolves. */
/* Colours follow the dashboard-wide brand rule — KO packs in reds, Campa
   packs in violets, other insurgents in neutrals — so the same brand reads
   the same colour on every tab. */
export const DEFAULT_SERIES = [
  { match: /sprite\s*zero.*250/i,      color: "#F4A0A6" },   // KO
  { match: /campa\s*lemon.*200/i,      color: "#C9B5EC" },   // Campa
  { match: /campa\s*orange.*200/i,     color: "#9B78D6" },   // Campa
  { match: /^fanta.*250/i,             color: "#E8001A" },   // KO
  { match: /campa\s*cola.*200/i,       color: "#5B2FA0" },   // Campa
  { match: /(tux|thums).*250/i,        color: "#A30012" },   // KO
  { match: /lahori.*160/i,             color: "#9E9E9E" },   // insurgent
  { match: /rim-?zim.*250/i,           color: "#6E5A4E" },   // insurgent
];

export const SERIES_FALLBACK_COLORS = [
  "#F4A0A6", "#C9B5EC", "#9B78D6", "#E8001A", "#5B2FA0",
  "#A30012", "#9E9E9E", "#6E5A4E", "#1F3C88", "#7B4FC4",
];

/* Brand family for packs outside the deck's default set */
export function brandFamilyColor(brand, i = 0) {
  const b = String(brand || "");
  const pick = (arr) => arr[i % arr.length];
  if (/campa/i.test(b)) return pick(["#7B4FC4", "#9B78D6", "#5B2FA0", "#C9B5EC"]);
  if (/pepsi|\bpci\b|^pc\b/i.test(b)) return pick(["#1F3C88", "#3E5BA9", "#7C93CF"]);
  if (/lahori|rim-?zim|dailee|tilo|davat|bindu|artos/i.test(b)) return pick(["#9E9E9E", "#6E5A4E", "#BDBDBD"]);
  return pick(["#E8001A", "#A30012", "#F4A0A6", "#C42020"]);   // KO / Coke family
}

/** Resolve the deck's default series against what the sheet actually holds. */
export function defaultSeriesKeys(series) {
  const picked = [];
  DEFAULT_SERIES.forEach(({ match, color }) => {
    const hit = series.find(s => match.test(`${s.brand} ${s.pack}`) && !picked.some(p => p.key === s.key));
    if (hit) picked.push({ ...hit, color });
  });
  if (picked.length) return picked;
  return series.slice(0, 8).map((s, i) => ({ ...s, color: SERIES_FALLBACK_COLORS[i] }));
}